/**
 * Fonction utilitaire pour construire les URLs d'images
 * Fonctionne avec app-secura.com en production
 */
import { getApiUrl, isDevelopment } from './env.js';

/**
 * Obtient l'URL de base du serveur API
 * Utilise app-secura.com en production
 */
const getApiBaseUrl = () => {
  return getApiUrl();
};

/**
 * Construit l'URL complète d'une image
 * @param {string} imagePath - Chemin relatif de l'image (ex: /uploads/image.png)
 * @returns {string} URL complète de l'image
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) {
    console.warn('⚠️ getImageUrl: imagePath est null ou undefined');
    return null;
  }
  
  // Si c'est déjà une URL complète (http/https) ou si c'est du base64
  if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
    const apiUrl = getApiUrl();

    // En dev local : servir les images depuis le serveur local (évite ERR_CERT sur app-secura.com)
    if (isDevelopment() && imagePath.includes('app-secura.com')) {
      try {
        const parsed = new URL(imagePath);
        return `${apiUrl}${parsed.pathname}${parsed.search}`;
      } catch {
        /* ignore */
      }
    }

    // Production - retourner l'URL telle quelle
    // Si l'URL contient des anciennes références ngrok ou localhost, les remplacer par l'API courante
    const oldPatterns = [
      /https?:\/\/kohen-subinternal-sobbingly\.ngrok-free\.dev/g,
      /https?:\/\/secura\.ngrok\.app/g,
      /https?:\/\/secura\.com\.ngrok\.app/g,
      /https?:\/\/secura\.com\.ngrok\.dev/g,
      /https?:\/\/[^\/]+\.ngrok-free\.dev/g,
      /https?:\/\/[^\/]+\.ngrok\.io/g,
      /http:\/\/localhost:\d+/g,
      /http:\/\/127\.0\.0\.1:\d+/g
    ];
    
    let cleanedUrl = imagePath;
    oldPatterns.forEach(pattern => {
      cleanedUrl = cleanedUrl.replace(pattern, apiUrl);
    });
    
    if (cleanedUrl !== imagePath) {
      console.log('🖼️ getImageUrl - URL corrigée vers production:', cleanedUrl);
      return cleanedUrl;
    }
    
    return imagePath;
  }
  
  // S'assurer que le chemin commence par /
  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  // Construire l'URL complète
  const apiBaseUrl = getApiBaseUrl();
  const finalUrl = apiBaseUrl ? `${apiBaseUrl}${cleanPath}` : cleanPath;
  
  // Debug temporaire pour diagnostiquer le problème
  console.log('🖼️ getImageUrl DEBUG:');
  console.log('  - imagePath:', imagePath);
  console.log('  - apiBaseUrl:', apiBaseUrl || '(vide = URL relative)');
  console.log('  - cleanPath:', cleanPath);
  console.log('  - finalUrl:', finalUrl);
  console.log('  - window.location.origin:', window.location.origin);
  console.log('  - window.location.hostname:', window.location.hostname);
  console.log('  - isDev:', import.meta.env.DEV);
  
  return finalUrl;
};

/**
 * Construit l'URL complète d'un logo d'entreprise
 * @param {string} logoPath - Chemin relatif du logo (ex: /uploadLogo/logo.png)
 * @returns {string} URL complète du logo
 */
export const getLogoUrl = (logoPath) => {
  return getImageUrl(logoPath);
};

/**
 * Construit l'URL complète d'un logo d'entreprise pour les cartes (dans uploadLogoEntreprises)
 * @param {string} logoPath - Chemin relatif du logo (ex: /uploadLogoEntreprises/logo.png)
 * @returns {string} URL complète du logo
 */
export const getLogoEntrepriseCarteUrl = (logoPath, apiBaseUrl = null) => {
  if (!logoPath) return null;

  const API_URL = (apiBaseUrl || getApiUrl()).replace(/\/$/, '');

  if (logoPath.startsWith('http') || logoPath.startsWith('data:')) {
    return logoPath;
  }

  if (logoPath.startsWith('/')) {
    return `${API_URL}${logoPath}`;
  }

  return `${API_URL}/uploadLogoEntreprises/${logoPath}`;
};

/**
 * Construit l'URL complète d'un logo d'entreprise pour les rapports (dans uploadLogo)
 * @param {string} logoPath - Chemin relatif du logo (ex: /uploadLogo/logo.png)
 * @returns {string} URL complète du logo
 */
export const getLogoRapportUrl = (logoPath) => {
  return getLogoUrl(logoPath);
};

/**
 * Construit l'URL complète d'une photo de profil
 * @param {string} photoPath - Chemin relatif de la photo (ex: /uploads/photo.png)
 * @returns {string} URL complète de la photo
 */
export const getPhotoUrl = (photoPath) => {
  return getImageUrl(photoPath);
};

/**
 * Construit l'URL complète d'une carte d'employé
 * @param {string} cardPath - Chemin relatif de la carte (ex: /uploads/card.png)
 * @returns {string} URL complète de la carte
 */
export const getCardUrl = (cardPath) => {
  return getImageUrl(cardPath);
};

